<template>
  <div
    class="blocks"
    ref="container"
    @scroll="onScroll">
    <component
      v-for="item in blocks" :key="item.poi"
      :is="blockType(item)"
      :info="item"
      :pin="pinNav"/>

    <FloatButtonGroup
    :back-top-visible="showFloatButtons"
    :items="floatButtons"
    @backTop="backTop"/>
  </div>
</template>

<script>
import PhotoBlock from "@/pages/Campaign/components/PhotoBlock"
import NavBlock from "@/pages/Campaign/components/NavBlock"
import FloatButtonGroup from '@/pages/Campaign/components/FloatButtonGroup'
import {BLOCK_TYPE} from "@/pages/Campaign/utils/enum"
import {calcScalePx750} from "@/pages/Campaign/utils/calcRem"
import {getCampaignDetail} from "@/pages/Campaign/api"
import {mapActions, mapMutations, mapState} from "vuex"
import Dayjs from 'dayjs'
import {wechatEnvironment} from "@/util";
import {UNI_ROUTE} from "@/pages/Campaign/utils/uniRoute";
import WxMiniShare from "@/pages/Campaign/mixins/wxMiniShare"

export default {
  name: "Land",
  components: {
    PhotoBlock,
    NavBlock,
    FloatButtonGroup
  },
  mixins: [WxMiniShare],
  data() {
    return {
      blocks: [],
      floatButtons: [],
      pinNav: false,
      // 目前只有一个导航区块
      navOffsetTop: 0,
      navHeight: 0,
      // 所有 block 默认情况下的顶部间距
      offsetTops: [],
      showFloatButtons: false,
    }
  },
  async mounted() {
    // 兼容微信内置浏览器已进入就会触发window.onresize, 延时一秒钟再触发
    // 其他情况延迟 300 毫秒
    const delay = wechatEnvironment ? 1000 : 300
    await new Promise(resolve => {
      setTimeout(() => resolve(true), delay)
    })

    window.addEventListener('resize', function() {
      window.location.reload()
    })

    await this.getCampaignDetail()
    this.calcOffsetTops()
    this.SET_BLOCKS(this.blocks)

    this.$sensors.track('page_begin', {
      page_name: `承接页-${document.title}`,
      from_page_name: document.referrer,
      cur_spm: '1001.21.0.0'
    })
  },
  beforeDestroy() {
    this.$sensors.track('page_end', {
      page_name: `承接页-${document.title}`,
      cur_spm: '1001.21.0.0'
    })

    window.removeEventListener('message', this.messageHandler)
  },
  computed: {
    ...mapState(['selectNavPhotoPoi'])
  },
  watch: {
    selectNavPhotoPoi(val) {
      this.$refs.container.scrollTop = this.offsetTops[val] - this.navHeight
    }
  },
  methods: {
    ...mapMutations(['SET_BLOCKS']),
    ...mapActions(['onPhotoScrolled']),
    async getCampaignDetail() {
      const id = this.$route.params.id
      const isPreview = this.$route.params.isPreview === '1'
      // route meta isEdit is true when the page is in edit mode
      const isEdit = this.$route.meta.isEdit

      // 如果为编辑状态，通过 message 传递数据，不需要从远程获取数据
      if (isEdit) {
        this.messageHandler = e => {
          const { type, data } = e.data
          if (type === 'edit-preview') {
            this.handleTemplateDate(data?.template)
            this.calcOffsetTops()
            this.SET_BLOCKS(this.blocks)
          }
        }

        window.addEventListener('message', this.messageHandler);
      } else {
        await UNI_ROUTE.getUniRouteConfig()

        const campaign = await getCampaignDetail(id, isPreview)
        document.title = campaign.name

        // 判断是否显示缺省页
        const now = new Dayjs()

        if (campaign.status === 'down' && !isPreview) {
          this.$router.push('/empty/offline')
          return
        }

        if (campaign.onlineTime) {
          const online = new Dayjs(campaign.onlineTime, 'YYYY-MM-DD HH:mm:ss')
          if (online.isAfter(now)) {
            this.$router.push('/empty/wait')
            return
          }
        }

        if (campaign.offlineTime) {
          const offline = new Dayjs(campaign.offlineTime, 'YYYY-MM-DD HH:mm:ss')
          if (offline.isBefore(now)) {
            this.$router.push('/empty/offline')
            return
          }
        }

        const template = campaign?.template?.template
        this.handleTemplateDate(template)
      }
    },
    handleTemplateDate(template) {
      if (!template) return

      const blocks = template.blocks?.sort((a, b) => a.poi - b.poi) || []

      const mappedBlocks = []

      for (let e of blocks) {
        if (e.type === BLOCK_TYPE.PHOTO) {
          const photoBlock = this.mapPhotoBlock(e)
          if (photoBlock) mappedBlocks.push(...photoBlock)
        } else if (e.type === BLOCK_TYPE.NAV) {
          const navBlock = this.mapNavBlock(e)
          if (navBlock) mappedBlocks.push(navBlock)
        }
      }

      // 因为 flat 图片区块， poi 存在重复的情况，按照索引值重新赋值
      mappedBlocks.forEach((e, index) => {
        e.poi = index
      })

      this.blocks = mappedBlocks

      // 获取 floatButton 数据
      this.floatButtons = (template.floatButtons || []).reverse()
    },
    // 组合 photoBlock 数据
    mapPhotoBlock(block) {
      // 第一版本 photoBlock 只含有一张图片
      if (block.img?.path) return [block]

      // 第二版 photoBlock 可包含多张图片，转换成只含一张图片的 photoBlock
      if (block.img?.length && block.img.length > 0) {
        const newBlocks = []

        for (let e of block.img) {
          const newBlock = { ...block }
          newBlock.img = e
          newBlocks.push(newBlock)
        }

        return newBlocks
      }

      return null
    },
    // navBlock 保存数据类型为平铺数组，各个层级的数据都在一个数组中
    // 这里组合成层级模式，方便在递归组件中绑定数据
    mapNavBlock(block) {
      if (!block.group) return null

      const levels = block.group.map(e => e.level)
      const maxLevel = Math.max(...levels)

      // 组合导航分组数据
      let resultArr = block.group.filter(e => e.level === maxLevel) || []
      let tempLevel = maxLevel

      while (tempLevel > 0) {
        const upperLevelGroups = block.group.filter(e => e.level === tempLevel - 1) || []
        if (upperLevelGroups.length === 0) break

        for (let group of upperLevelGroups) {
          if (!group.items) break
          for (let item of group.items) {
            item.group = resultArr.find(e => e.parentItemId === item.id)
          }
        }
        resultArr = upperLevelGroups
        tempLevel--
      }

      block.group = resultArr.length > 0 ? resultArr[0] : []
      return block
    },
    calcOffsetTops() {
      let sum = 0
      this.blocks.forEach(e => {
        this.offsetTops.push(sum)

        if (e.type === BLOCK_TYPE.NAV) {
          this.navOffsetTop = sum
          this.navHeight = calcScalePx750(e.baseBg?.height || 0)
        }
        let height = e.type === BLOCK_TYPE.PHOTO ? e.img?.height : e.baseBg?.height
        sum += calcScalePx750(height || 0)
      })
    },
    blockType(block) {
      return block.type === BLOCK_TYPE.NAV ? 'NavBlock' : 'PhotoBlock'
    },
    onScroll(e) {
      const scrollTop = e.target.scrollTop
      this.pinNav = this.navOffsetTop > 0 && scrollTop > this.navOffsetTop
      this.showFloatButtons = scrollTop > window.screen.height

      const lastOffset = this.offsetTops[this.offsetTops.length - 1] || Number.MAX_VALUE

      let currentBlockIndex = -1

      for (let i = 0; i < this.offsetTops.length; i++) {
        const offset = this.offsetTops[i] - this.navHeight
        // 尺寸换算可能会有 1 个误差点
        if (Math.abs(scrollTop - offset) < 1) {
          currentBlockIndex = i
          break
        } else if (scrollTop > lastOffset) {
          // 如果大于最后一张图的 offset，则为最后一张图的索引
          currentBlockIndex = this.offsetTops.length - 1
          break
        } else if (scrollTop < offset) {
          // 如果 scrollTop 小于某一张图的 offsetTop，表明正处于上一张图的区域
          currentBlockIndex = i - 1
          break
        }
      }

      const currentBlock = this.blocks?.[currentBlockIndex]
      if (currentBlock?.type === BLOCK_TYPE.PHOTO) {
        this.onPhotoScrolled(currentBlock.poi)
      }
    },
    backTop() {
      this.$refs.container.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })

      this.$sensors.track('to_top_click', {
        page_name: `承接页-${document.title}`,
        text: '置顶',
        cur_spm: `1001.${this.$route.params.id}.loc4.1`
      })
    }
  }
}
</script>

<style scoped>
.blocks {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  font-size: 0;
}
</style>
